import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';


const AchievementBadges = ({ limit, showAll = false }) => {
  const [achievements, setAchievements] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchAchievements();
  }, []);

  const fetchAchievements = async () => {
    // Mock data - replace with actual Supabase queries
    const mockAchievements = [
      {
        id: 1,
        name: 'Early Bird',
        description: 'Check in before 7 AM for 5 consecutive days',
        category: 'attendance',
        icon: 'Sunrise',
        unlocked: true,
        unlockedDate: '2026-01-15',
        points: 100,
        rarity: 'common'
      },
      {
        id: 2,
        name: 'Workout Warrior',
        description: 'Complete 50 workout sessions',
        category: 'workout',
        icon: 'Dumbbell',
        unlocked: true,
        unlockedDate: '2026-01-20',
        points: 250,
        rarity: 'rare'
      },
      {
        id: 3,
        name: 'Class Champion',
        description: 'Attend 25 group fitness classes',
        category: 'classes',
        icon: 'Users',
        unlocked: true,
        unlockedDate: '2026-01-18',
        points: 200,
        rarity: 'uncommon'
      },
      {
        id: 4,
        name: 'Referral Master',
        description: 'Refer 5 friends who join the gym',
        category: 'referral',
        icon: 'UserPlus',
        unlocked: false,
        progress: 60,
        points: 500,
        rarity: 'epic'
      },
      {
        id: 5,
        name: 'Streak Legend',
        description: 'Maintain a 30-day check-in streak',
        category: 'attendance',
        icon: 'Flame',
        unlocked: false,
        progress: 40,
        points: 300,
        rarity: 'rare'
      },
      {
        id: 6,
        name: 'Community Star',
        description: 'Get 100 likes on your posts',
        category: 'community',
        icon: 'Heart',
        unlocked: false,
        progress: 75,
        points: 150,
        rarity: 'uncommon'
      }
    ];
    setAchievements(mockAchievements);
  };

  const categories = [
    { value: 'all', label: 'All Achievements' },
    { value: 'attendance', label: 'Attendance' },
    { value: 'workout', label: 'Workouts' },
    { value: 'classes', label: 'Classes' },
    { value: 'referral', label: 'Referrals' },
    { value: 'community', label: 'Community' }
  ];

  const getRarityColor = (rarity) => {
    const colors = {
      common: 'text-muted-foreground',
      uncommon: 'text-success',
      rare: 'text-primary',
      epic: 'text-accent',
      legendary: 'text-warning'
    };
    return colors?.[rarity] || colors?.common;
  };

  const filteredAchievements = selectedCategory === 'all'
    ? achievements
    : achievements?.filter(a => a?.category === selectedCategory);

  const displayedAchievements = showAll ? filteredAchievements : filteredAchievements?.slice(0, limit || 6);

  return (
    <div className="card">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-heading font-semibold text-foreground mb-1">
            Achievements
          </h2>
          <p className="text-sm text-muted-foreground">
            Unlock badges by completing challenges
          </p>
        </div>
        {showAll && (
          <div className="flex gap-2 flex-wrap">
            {categories?.map((cat) => (
              <button
                key={cat?.value}
                onClick={() => setSelectedCategory(cat?.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === cat?.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {cat?.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {displayedAchievements?.map((achievement) => (
          <div
            key={achievement?.id}
            className={`p-4 rounded-lg border-2 transition-all duration-250 ${
              achievement?.unlocked
                ? 'border-primary/30 bg-primary/5 hover:border-primary/50' :'border-border bg-muted/30 opacity-75'
            }`}
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  achievement?.unlocked ? 'bg-primary/20' : 'bg-muted'
                }`}
              >
                <Icon
                  name={achievement?.icon}
                  size={24}
                  color={achievement?.unlocked ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-semibold text-foreground text-sm">
                    {achievement?.name}
                  </h3>
                  {achievement?.unlocked && (
                    <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  {achievement?.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-medium capitalize ${getRarityColor(achievement?.rarity)}`}>
                    {achievement?.rarity}
                  </span>
                  <span className="text-xs font-medium text-primary">
                    +{achievement?.points} pts
                  </span>
                </div>
                {!achievement?.unlocked && achievement?.progress && (
                  <div className="mt-2">
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all duration-500"
                        style={{ width: `${achievement?.progress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {achievement?.progress}% complete
                    </p>
                  </div>
                )}
                {achievement?.unlocked && achievement?.unlockedDate && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Unlocked {new Date(achievement?.unlockedDate)?.toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AchievementBadges;